import { dateStringLongMonth } from 'mewe/utils/datetime-utils';
import Component from '@glimmer/component';
import { computed, action } from '@ember/object';

import PS from 'mewe/utils/pubsub';
import { StickyController } from 'mewe/utils/elements-utils';
import Storage from 'mewe/shared/storage';
import dispatcher from 'mewe/dispatcher';
import { ds } from 'mewe/stores/ds';
import { fetchUsersCounters } from 'mewe/fetchers/fetch-followers';
import { inject as service } from '@ember/service';
import { showNewToMeWePopup } from 'mewe/utils/dialogs-common';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import FollowingApi from 'mewe/api/following-api';
import { addObserver, removeObserver } from '@ember/object/observers';
import FunctionalUtils from 'mewe/shared/functional-utils';
import ChatStore from 'mewe/stores/chat-store';
import ThreadFactory from 'mewe/stores/models/thread-model';
import CurrentUserStore from 'mewe/stores/current-user-store';
import ReputationApi from 'mewe/api/reputation-api';
import { TellerType } from 'mewe/constants';
import { toTeller } from 'mewe/fetchers/fetch-stories';

export default class ProfileMain extends Component {
  @service chat;
  @service tasks;
  @service account;
  @service router;
  @service dynamicDialogs;
  @service features;

  @tracked showSettingsDropdown;
  @tracked avatarTask;
  @tracked coverTask;
  @tracked dialogTab;
  @tracked usersCounters = ds.usersCounters;
  @tracked mutualConnectionsResponse;
  @tracked reputationPoints = 0;

  @computed('args.contact.isFetching', 'args.contact.profile.textServer')
  get descriptionAndMemberSince() {
    return `${this.description}${this.description.length ? '\n\n' : ''}${this.memberSinceText}`;
  }

  @computed('mutualContactsCount.length', 'mutualContacts')
  get isUserHidesFollowers() {
    return this.mutualContactsCount && this.mutualContacts.length === 0;
  }

  get contact() {
    return this.args.contact;
  }

  get description() {
    return this.args.contact.profile?.textServer || '';
  }

  get badges() {
    return this.args.contact.badges;
  }

  get isPrivate() {
    return this.args.contact.public === false;
  }

  avatarDialogTitles = {
    choosePhoto: __('Choose Profile Photo'),
    cropPhoto: __('Edit Profile Photo'),
  };

  coverDialogTitles = {
    choosePhoto: __('Choose Background Photo'),
    cropPhoto: __('Edit Background Photo'),
  };

  constructor() {
    super(...arguments);
    this.avatarTask = this.tasks.getTask('photo.avatar.contacts');
    this.coverTask = this.tasks.getTask('photo.cover.contacts');
    this.selectPhotoAvatarBind = this.selectPhotoAvatar.bind(this);
    this.selectPhotoCoverBind = this.selectPhotoCover.bind(this);
    this.userBlockedHandlerBind = this.userBlockedHandler.bind(this);
    PS.Sub('user.blocked', this.userBlockedHandlerBind);
    addObserver(this, 'args.uuid', this.loadMutualConnections.perform);
    this.loadMutualConnections.perform();
  }

  @task
  *loadMutualConnections() {
    // don't try to make request on public profile
    if (!this.args.isMyProfile && !this.args.isPublicContent) {
      this.mutualConnectionsResponse = yield FollowingApi.getFollowersOf({ userIds: this.args.uuid });
    } else {
      this.mutualConnectionsResponse = null;
    }
  }

  @computed('mutualConnectionsResponse')
  get mutualContactsCount() {
    return this.mutualConnectionsResponse?.results?.[0]?.total || 0;
  }

  @computed('mutualConnectionsResponse')
  get mutualContacts() {
    return this.mutualConnectionsResponse?.users || [];
  }

  get memberSinceText() {
    if (this.contact.registeredAt) {
      return __('Member since {date}', {
        date: dateStringLongMonth(this.contact.registeredAt, this.account.activeUser.locale),
      });
    } else {
      return '';
    }
  }

  @computed('isUserFollowedByMe', 'isMyProfile')
  get allowNsfwContent() {
    if (this.isUserFollowedByMe) {
      return true;
    }
    if (this.isMyProfile) {
      return true;
    }
    const isNsfwAllowed = Storage.get(Storage.keys.allowNsfwContentSetting);
    if (isNsfwAllowed) {
      return true;
    }
    return false;
  }

  @computed('account.activeUser.id', 'contact.id', 'args.uuid')
  get isMyProfile() {
    return this.contact && this.contact.id === this.account.activeUser.id;
  }

  @computed('args.entries.items.length')
  get hasEntries() {
    return this.args.entries?.items?.length > 0;
  }

  @computed('args.entries.items.length')
  get hasNewEntries() {
    return this.args.entries?.items?.filterBy('isNew', true).length > 0;
  }

  @computed('mutualContacts.@each.publicLinkId', 'mutualContactsCount')
  get followersTextPartOne() {
    let handle = this.mutualContacts[0]?.publicLinkId;
    if (this.mutualContactsCount === 1) {
      return __(`Followed by <strong>{handle}</strong>.`, { handle });
    } else if (this.mutualContactsCount >= 2) {
      return __(`Followed by <strong>{handle}</strong>`, { handle });
    }
  }

  @computed('mutualContacts.@each.publicLinkId', 'mutualContactsCount')
  get followersTextPartTwo() {
    let handle = this.mutualContacts[0]?.publicLinkId;
    let count = this.mutualContactsCount - 1;
    if (this.mutualContactsCount === 2) {
      return __(` and <strong>{count} other</strong>.`, { handle, count });
    } else if (this.mutualContactsCount >= 2) {
      return __(` and <strong>{count} others</strong>.`, { handle, count });
    }
  }

  get canShowInfo() {
    if (this.args.flagNewProfiles) return false;
    return this.contact.isMe || this.contact.public || this.contact.following;
  }

  get canChat() {
    return this.contact.canChat && !this.contact.isMe;
  }

  get isUserFollowedByMe() {
    return this.contact.following;
  }

  get canSeeFollowers() {
    return (this.contact.following && !this.contact.hideFollowers) || this.contact.public || this.isMyProfile;
  }

  get hasFollowRequests() {
    return this.usersCounters?.requestsReceived && this.isMyProfile;
  }

  get isReputationEnabled() {
    return this.features.isEnabled('reputation');
  }

  get showReputationScore() {
    return this.isReputationEnabled && this.contact.isMe && this.contact.public;
  }

  get showReputationButton() {
    return this.isReputationEnabled && !this.contact.isMe;
  }

  userBlockedHandler(data) {
    if (data.userId === this.contact.id) {
      this.router.transitionTo('app.myworld');
    }
  }

  openJournalsDialog() {
    const tellerData = toTeller({
      id: this.contact.id,
      storytellerType: TellerType.USER,
    });
    tellerData.userStoryteller = this.contact; // contact is EmberObject, can't be serialized

    const params = {
      tellerData,
      context: 'profile_feed',
    };

    this.dynamicDialogs.openDialog('stories-dialog', params);
  }

  @action
  shareProfile() {
    this.dynamicDialogs.openDialog('quick-share-profile-dialog', {
      dialogId: 'quick-share-profile-dialog',
      user: this.contact,
    });
  }

  @action
  openFollowingDialog(tab) {
    if (this.args.isPublicContent) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    if (this.hasFollowRequests) {
      this.dialogTab = 'request';
      this.openFollowerDialog();
    }

    if (this.canSeeFollowers) {
      this.dialogTab = tab;
      this.openFollowerDialog();
    }
  }

  @action
  openMutualDialog() {
    this.dialogTab = 'mutual';
    this.openFollowerDialog();
  }

  @action
  displayopenFollowerDialogPopup() {
    if (this.args.isPublicContent) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    this.openFollowerDialog();
  }

  @action
  showNewToMeWePopup() {
    this.args.isPublicContent && showNewToMeWePopup(this.dynamicDialogs);
  }


  @action
  openAvatarPhoto() {
    if (this.args.isPublicContent) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    let photoUrl = this.contact._links.avatar.href;

    this.dynamicDialogs.openDialog('media-dialog', {
      photoUrl: photoUrl,
      mediaType: 'photoUrl',
      allowMultipleInstances: true,
    });
  }

  @action
  openProfileStories() {
    if (this.args.isPublicContent) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    this.openJournalsDialog();
  }

  @action
  openCoverPhoto() {
    if (this.args.isPublicContent) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    let photoUrl = this.contact._links.cover.href;

    this.dynamicDialogs.openDialog('media-dialog', {
      photoUrl: photoUrl,
      mediaType: 'photoUrl',
      allowMultipleInstances: true,
    });
  }

  selectPhotoAvatar(blob, params) {
    dispatcher.dispatch('contact', 'setAvatar', blob, params, this.avatarTask);
  }

  selectPhotoCover(blob, params) {
    dispatcher.dispatch('contact', 'setPublicCover', blob, params, this.coverTask);
  }

  @action
  openChat() {
    this.chat.openThreadByParticipants([this.contact], { doNotClose: true });
  }

  @action
  showSettings() {
    if (this.isMyProfile) {
      this.router.transitionTo('app.settings.privacy');
      return;
    }
  }

  @action
  openFollowerDialog() {
    this.dynamicDialogs.openDialog('followers-dialog', {
      uuid: this.args.uuid,
      currentTab: this.dialogTab,
      title: this.contact.name,
      isPublicProfile: this.contact.public,
      contactId: this.contact.id,
    });
  }

  @action
  async onInsert(element) {
    this.stickyController = new StickyController({ element: element });

    if (!this.args.isPublicContent) {
      fetchUsersCounters();
    }

    if (this.isReputationEnabled && this.contact.isMe && this.contact.public) {
      const reputation = await ReputationApi.getReputation(this.contact.id);
      this.reputationPoints = reputation.points;
    }
  }

  @action
  async onUpdate() {
    if (this.isReputationEnabled && this.contact.isMe && this.contact.public) {
      const reputation = await ReputationApi.getReputation(this.contact.id);
      this.reputationPoints = reputation.points;
    }
  }

  @action
  onDestroy() {
    PS.Unsub('user.blocked', this.userBlockedHandlerBind);
    removeObserver(this, 'args.uuid', this.loadMutualConnections.perform);
  }

  @action
  async startCall(videoEnabled) {
    const thread = await this.chat.getThreadByParticipants([this.contact]);
    if (thread?.id) {
      dispatcher.dispatch('chat', 'openCall', {
        thread: thread,
        videoEnabled: videoEnabled,
      });
    } else {
      // thread creation callback
      const startCallCallback = (thread) => {
        if (thread && thread.id) {
          dispatcher.dispatch('chat', 'openCall', {
            thread: thread,
            videoEnabled: videoEnabled,
          });
        } else {
          FunctionalUtils.showDefaultErrorMessage();
        }
      };
      // thread doesn't exist yet, has to be created before starting a call
      let newThread = this.createNewThread();
      dispatcher.dispatch('chat', 'sendMessage', newThread, {
        createWithoutMsg: true,
        doneCallback: startCallCallback,
      });
    }
  }

  createNewThread() {
    const Thread = ThreadFactory(CurrentUserStore, ChatStore);
    return Thread.create({
      isNewChat: true,
      messagesInitied: true,
      startedBy: this.account.activeUser.id,
      participants: [this.contact],
      waitingForCreation: true,
      expandedChatSize: false
    });
  }
}
